/**
 * Image upload field — tries Cloudinary first (when configured), otherwise
 * falls back to our own backend /api/admin/media/upload endpoint, which stores
 * the file on disk and returns a public URL. URL paste is always available.
 */
import React, { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import api from '../lib/api';

const CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const CLOUDINARY_ENABLED = !!(CLOUD && PRESET);

export default function ImageUploadField({ value, onChange, label = 'Image', testidPrefix = 'image' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  async function upload(file) {
    setErr(''); setUploading(true);
    try {
      if (CLOUDINARY_ENABLED) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('upload_preset', PRESET);
        const r = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD}/image/upload`, { method: 'POST', body: fd });
        const data = await r.json();
        if (data.secure_url) { onChange(data.secure_url); return; }
        throw new Error(data.error?.message || 'Cloudinary upload failed');
      }
      // Backend upload fallback
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.url) onChange(r.data.url);
      else throw new Error('Upload failed');
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <div className="label mb-1.5">{label}</div>
      {value && (
        <div className="relative inline-block mb-3">
          <img src={value} alt="" className="h-32 rounded-xl object-cover border border-charcoal/10" />
          <button type="button" onClick={() => onChange('')} className="absolute -top-2 -right-2 bg-heritage text-base rounded-full p-1 hover:bg-heritage-deep" data-testid={`${testidPrefix}-clear`}>
            <X size={12}/>
          </button>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); }} data-testid={`${testidPrefix}-file`}/>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testidPrefix}-upload-btn`}
          className="bg-admin border border-charcoal/15 hover:border-forest rounded-xl px-4 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Upload size={14}/> {uploading ? 'Uploading...' : value ? 'Replace image' : 'Upload image'}
        </button>
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="or paste an image URL"
          className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-forest"
          data-testid={`${testidPrefix}-url`}
        />
        {err && <div className="text-xs text-heritage">{err}</div>}
      </div>
    </div>
  );
}
